import { useEffect, useRef } from 'react';
import { Player } from '../types';
import './GameOver.css';

interface Props {
  players: Player[];
}

const MEDAL_EMOJI  = ['🥇', '🥈', '🥉'];
const MEDAL_COLORS = ['#ffd700', '#c0c0c0', '#cd7f32'];
const TEAM_COLORS  = ['#1565c0', '#b71c1c', '#2e7d32'];
const PODIUM_HEIGHTS = [120, 80, 50];
const CONFETTI_COLORS = ['#ffd700', '#fff176', '#ffffff', '#82b1ff', '#ff8a80', '#b9f6ca', '#ffca28'];

function formatScore(score: number) {
  if (score < 0) return `-$${Math.abs(score).toLocaleString()}`;
  return `$${score.toLocaleString()}`;
}

export default function GameOver({ players }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const outroRef = useRef<HTMLAudioElement>(null);

  // Play the outro fanfare when the results screen mounts.
  useEffect(() => {
    if (outroRef.current) {
      outroRef.current.volume = 0.65;
      outroRef.current.play().catch(() => {});
    }
  }, []);

  // Assign dense rank: rank = number of players with strictly higher score
  const ranked = [...players]
    .map((p, i) => ({ ...p, originalIndex: i }))
    .sort((a, b) => b.score - a.score)
    .map((p, _, arr) => ({
      ...p,
      rank: arr.filter(r => r.score > p.score).length,
    }));

  const winners = ranked.filter(p => p.rank === 0);
  const isTie = winners.length > 1;

  // Podium display order: 2nd-place left, 1st-place center, 3rd-place right
  // For ties, use sorted order but center position still gets the top scorer(s)
  const displayOrder = (() => {
    const r0 = ranked[0]; // highest
    const r1 = ranked[1]; // middle
    const r2 = ranked[2]; // lowest
    if (!r1) return [r0];
    if (!r2) return [r1, r0];
    return [r1, r0, r2];
  })();

  // Canvas confetti
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let running = true;

    function resize() {
      canvas!.width = window.innerWidth;
      canvas!.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    interface Piece {
      x: number; y: number; w: number; h: number;
      color: string; vx: number; vy: number;
      angle: number; va: number; opacity: number;
    }

    const pieces: Piece[] = [];
    const MAX = 180;
    let lastSpawn = 0;

    function spawn() {
      const x = Math.random() * canvas!.width;
      for (let i = 0; i < 3; i++) {
        pieces.push({
          x, y: -10,
          w: 6 + Math.random() * 10, h: 4 + Math.random() * 6,
          color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
          vx: (Math.random() - 0.5) * 3, vy: 2 + Math.random() * 3,
          angle: Math.random() * Math.PI * 2, va: (Math.random() - 0.5) * 0.14,
          opacity: 0.9,
        });
      }
    }

    function draw(t: number) {
      if (!running) return;
      ctx!.clearRect(0, 0, canvas!.width, canvas!.height);
      if (t - lastSpawn > 60 && pieces.length < MAX) { spawn(); lastSpawn = t; }

      for (let i = pieces.length - 1; i >= 0; i--) {
        const p = pieces[i];
        p.x += p.vx; p.y += p.vy; p.angle += p.va; p.vy += 0.05;
        if (p.y > canvas!.height * 0.7) p.opacity = Math.max(0, p.opacity - 0.015);
        if (p.y > canvas!.height + 20 || p.opacity <= 0) { pieces.splice(i, 1); continue; }
        ctx!.save();
        ctx!.globalAlpha = p.opacity;
        ctx!.translate(p.x, p.y);
        ctx!.rotate(p.angle);
        ctx!.fillStyle = p.color;
        ctx!.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx!.restore();
      }
      animId = requestAnimationFrame(draw);
    }

    animId = requestAnimationFrame(draw);
    return () => { running = false; cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return (
    <div className="gameover">
      <canvas ref={canvasRef} className="gameover-canvas" />

      <div className="gameover-content">
        <div className="gameover-title">
          {'GAME OVER'.split('').map((ch, i) => (
            <span key={i} className="go-letter" style={{ animationDelay: `${i * 70}ms` }}>
              {ch === ' ' ? '\u00A0' : ch}
            </span>
          ))}
        </div>

        <div className="gameover-subtitle">Happy 90th Birthday, Gamy!</div>

        {/* Winner callout — handles ties */}
        <div className="gameover-winner">
          <div className="gw-crown">{isTie ? '🤝' : '👑'}</div>
          <div className="gw-label">{isTie ? "IT'S A TIE!" : 'WINNER'}</div>
          <div className={`gw-names ${isTie ? 'gw-names-tie' : ''}`}>
            {winners.map((w, i) => (
              <span
                key={w.originalIndex}
                className="gw-name"
                style={{ color: TEAM_COLORS[w.originalIndex] ?? '#ffd700' }}
              >
                {w.name}{isTie && i < winners.length - 1 ? <span className="gw-tie-amp"> &amp; </span> : null}
              </span>
            ))}
          </div>
          <div className="gw-score">{formatScore(winners[0].score)}</div>
        </div>

        {/* Podium — 2nd left / 1st center / 3rd right */}
        <div className="gameover-podium">
          {displayOrder.map((player, displayIdx) => {
            const rank = player.rank;
            const medalColor = MEDAL_COLORS[Math.min(rank, 2)];
            const blockHeight = PODIUM_HEIGHTS[Math.min(rank, 2)];
            const isTopRank = rank === 0;
            return (
              <div
                key={player.originalIndex}
                className={`go-podium-col go-rank-${rank + 1}`}
                style={{ animationDelay: `${0.8 + displayIdx * 0.15}s` }}
              >
                <div className="go-podium-medal">
                  {MEDAL_EMOJI[Math.min(rank, 2)]}
                </div>
                <div className="go-podium-name">{player.name}</div>
                <div
                  className={`go-podium-score ${isTopRank ? 'go-winner-score' : ''}`}
                  style={{ color: isTopRank ? '#ffd700' : '#ccc' }}
                >
                  {formatScore(player.score)}
                </div>
                <div
                  className="go-podium-block"
                  style={{
                    height: blockHeight,
                    background: `linear-gradient(to bottom, ${medalColor}33, ${medalColor}11)`,
                    borderTop: `3px solid ${medalColor}`,
                  }}
                >
                  <span className="go-podium-rank">{rank + 1}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <audio ref={outroRef} src="/sounds/outro.mp3" preload="auto" />
    </div>
  );
}
